import { useState, type FormEvent } from "react";
import Cookies from "js-cookie";

import { Button, FileField, Modal, Select } from "../../components/ui/index.ts";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { useSqeLabs } from "../../hooks/use-sqe-labs.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import {
  parseSalaryUploadResult,
  salaryUploadErrorMessage,
  uploadSalaryFile,
  type SalaryUploadResult,
} from "./salary-upload-api.ts";

import styles from "./upload-salary.module.css";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const MONTH_OPTIONS = MONTHS.map((month) => ({ value: month, label: month }));
const EMPLOYEE_TYPE_OPTIONS = [
  { value: "CONTRACT", label: "Consultant" },
  { value: "REGULAR", label: "Regular" },
];
const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function failureToastMessage(result: SalaryUploadResult): string {
  const count = `${result.failed} salary ${result.failed === 1 ? "row" : "rows"} failed.`;
  return result.failures[0] ? `${count} ${result.failures[0].message}` : count;
}

export default function UploadSalary() {
  const isSqe = useSqeLabs();
  const [employeeType, setEmployeeType] = useState("");
  const [month, setMonth] = useState("");
  const [salarySlip, setSalarySlip] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState<SalaryUploadResult | null>(null);

  const resetForm = () => {
    setEmployeeType("");
    setMonth("");
    setSalarySlip(null);
    setFileInputKey((key) => key + 1);
    setUploadResult(null);
  };

  const handleFileChange = (file: File | null) => {
    if (file && file.type !== XLSX_TYPE) {
      showError("Unsupported file type. Please select a valid .xlsx file.");
      setSalarySlip(null);
      setFileInputKey((key) => key + 1);
      return;
    }
    setSalarySlip(file);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!salarySlip || !month || (!isSqe && !employeeType)) return;

    setIsLoading(true);
    try {
      const response = await uploadSalaryFile({
        file: salarySlip,
        month,
        employeeType,
        isSqe,
        accessToken: Cookies.get("accessToken"),
      });
      const result = isSqe ? parseSalaryUploadResult(response.data) : null;
      if (result && result.failed > 0) {
        setUploadResult(result);
        showError(failureToastMessage(result));
        return;
      }
      resetForm();
      showSuccess("Successfully Uploaded!");
    } catch (error) {
      console.error(error);
      const result = isSqe ? parseSalaryUploadResult(error) : null;
      if (result && result.failed > 0) {
        setUploadResult(result);
      }
      showError(result && result.failed > 0
        ? failureToastMessage(result)
        : isSqe ? salaryUploadErrorMessage(error) : "Something went wrong. Please try again!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageShell>
      <section className={styles.card} aria-labelledby="upload-salary-heading">
        <form onSubmit={handleSubmit} className={styles.form}>
          {!isSqe && (
            <Select
              id="employeeType"
              label="Employee Type"
              required
              placeholder="Select Employee Type"
              value={employeeType}
              onChange={setEmployeeType}
              options={EMPLOYEE_TYPE_OPTIONS}
              disabled={isLoading}
            />
          )}
          <FileField
            key={fileInputKey}
            id="salarySlip"
            label="Salary Slip"
            hint="Note: Please upload .xlsx file only."
            accept=".xlsx"
            required
            onChange={handleFileChange}
            disabled={isLoading}
          />
          <Select
            id="month"
            label="Month"
            required
            placeholder="Select Month"
            value={month}
            onChange={setMonth}
            options={MONTH_OPTIONS}
            disabled={isLoading}
          />
          <div className={styles.actions}>
            <Button type="button" variant="light-grey" onClick={resetForm} disabled={isLoading}>
              Reset
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Uploading..." : "Submit"}
            </Button>
          </div>
        </form>
      </section>
      <Modal
        open={uploadResult !== null}
        onClose={() => setUploadResult(null)}
        title="Salary Upload Errors"
        size="md"
        footer={<Button type="button" onClick={() => setUploadResult(null)}>Close</Button>}
      >
        {uploadResult && (
          <div className={styles.resultContent}>
            <p className={styles.resultSummary}>
              {uploadResult.failed} of {uploadResult.totalRows} rows failed.
              {" "}{uploadResult.created} created, {uploadResult.updated} updated.
            </p>
            {uploadResult.failures.length > 0 ? (
              <ul className={styles.failureList}>
                {uploadResult.failures.map((failure, index) => (
                  <li className={styles.failureCard} key={`${failure.rowNumber ?? "row"}-${index}`}>
                    <p className={styles.failureHeading}>
                      {failure.rowNumber !== null ? `Row ${failure.rowNumber}` : `Failed row ${index + 1}`}
                      {failure.employeeId !== null ? ` · Employee ${failure.employeeId}` : ""}
                    </p>
                    <p className={styles.failureMessage}>{failure.message}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p>The server did not provide details for the failed rows.</p>
            )}
          </div>
        )}
      </Modal>
    </PageShell>
  );
}
